import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentSection: string;
  setCurrentSection: (section: string) => void;
}

export default function Sidebar({ isOpen, setIsOpen, currentSection, setCurrentSection }: SidebarProps) {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const userPhone = localStorage.getItem('userPhone');
    const superAdmins = ['97800237', '92252664'];
    setIsAdmin(userPhone ? superAdmins.includes(userPhone) : false);
  }, []);

  const menuItems = [
    { id: 'dashboard',      label: 'Tableau de bord',  icon: 'ri-dashboard-line' },
    { id: 'revenus',        label: 'Revenus',           icon: 'ri-money-dollar-circle-line' },
    { id: 'filleuls',       label: 'Mes filleuls',      icon: 'ri-user-follow-line' },
    { id: 'chaine',         label: 'Ma chaîne',         icon: 'ri-links-line' },
    { id: 'formation',      label: 'Formation',         icon: 'ri-book-open-line' },
    { id: 'taches',         label: 'Toutes les tâches', icon: 'ri-task-line' },
    { id: 'retraits',       label: 'Retraits',          icon: 'ri-bank-card-line' },
    { id: 'profil',         label: 'Moi',               icon: 'ri-user-line' },
    { id: 'roue',           label: 'Roue de chance',    icon: 'ri-trophy-line' },
    { id: 'contacts',       label: 'Contacts VCF',      icon: 'ri-contacts-book-line' },
    { id: 'apk',            label: 'APK Premium',       icon: 'ri-smartphone-line' },
    { id: 'apropos',        label: 'À propos de nous',  icon: 'ri-information-line' },
    { id: 'service-client', label: 'Service Client',    icon: 'ri-customer-service-2-line' },
  ];

  const handleMenuClick = (id: string) => {
    setCurrentSection(id);
    setIsOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('userPhone');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userName');
    localStorage.removeItem('userUsername');
    localStorage.removeItem('userCountry');
    sessionStorage.removeItem('adminPhone');
    sessionStorage.removeItem('adminPassword');
    navigate('/connexion');
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setIsOpen(false);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`
          fixed left-0 w-64 bg-white border-r border-gray-100 shadow-lg z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{ top: '2.25rem', bottom: 0, display: 'flex', flexDirection: 'column' }}
      >
        {/* Logo */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
                alt="SUCCESSLINK"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <p className="text-sm font-bold text-red-600 leading-tight tracking-wide">SUCCESSLINK</p>
              <p className="text-xs text-gray-400 font-medium">AGENCY</p>
            </div>
          </Link>
        </div>

        {/* Navigation scrollable */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {isAdmin && (
            <button
              onClick={handleAdminClick}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all cursor-pointer bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 mb-2"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                <i className="ri-shield-star-line text-base text-red-600"></i>
              </div>
              <span className="text-sm font-bold">Administration</span>
            </button>
          )}

          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left
                transition-all duration-150 cursor-pointer
                ${currentSection === item.id
                  ? 'bg-red-50 text-red-600 font-semibold shadow-sm border border-red-100'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                currentSection === item.id ? 'bg-red-100' : 'bg-gray-100'
              }`}>
                <i className={`${item.icon} text-base`}></i>
              </div>
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Bouton déconnexion — toujours visible en bas */}
        <div className="flex-shrink-0 px-3 py-3 border-t border-gray-100 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left bg-red-50 text-red-600 hover:bg-red-100 transition-all cursor-pointer border border-red-100"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
              <i className="ri-logout-box-line text-base text-red-600"></i>
            </div>
            <span className="text-sm font-bold">Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
}
